import { useLatest } from 'ahooks';
import { type EChartsType } from 'echarts/core';
import { type EChartsOption } from 'echarts/types/dist/shared';
import React, { useEffect, useRef, useState } from 'react';
import { isChartTestMode } from '../utils/consts';
import { loadEcharts } from '../utils/core';

interface IChartProps {
    width: number;
    height: number;
    options: EChartsOption;
    renderer?: 'svg' | 'canvas';
    onRenderEnd?: () => void;
}

const Chart: React.FC<IChartProps> = ({ width, height, options, renderer = 'canvas', onRenderEnd }) => {
    const containerElRef = useRef<HTMLDivElement>(null);
    const [echartInstance, setEchartInstance] = useState<EChartsType | null>(null);
    const echartInstanceRef = useLatest(echartInstance);
    if (isChartTestMode) {
        globalThis.echartInstance = echartInstance;
    }
    const onRenderEndRef = useLatest(onRenderEnd);
    const isInitRenderRef = useRef(true);

    useEffect(() => {
        if (!containerElRef.current) return;
        loadEcharts().then((echarts) => {
            const echartInstance = echarts.init(containerElRef.current, null, {
                renderer,
            });
            setEchartInstance(echartInstance);
        });
    }, [containerElRef, renderer]);

    useEffect(() => {
        if (!echartInstance) return;
        /* eslint-disable-next-line */
        // @ts-ignore
        echartInstance.setOption(
            {
                ...options,
                animation: isInitRenderRef.current,
            },
            {
                notMerge: true,
                silent: true,
                lazyUpload: true,
            },
        );
        if (isInitRenderRef.current) {
            echartInstance.on('finished', () => {
                onRenderEndRef.current?.();
            });
            isInitRenderRef.current = false;
        }
    }, [JSON.stringify(options), echartInstance]);

    useEffect(() => {
        echartInstanceRef.current?.resize();
    }, [width, height]);

    return <div ref={containerElRef} style={{ width, height }} />;
};

Chart.displayName = 'Chart';

export default Chart;
